import React, { useState, useEffect, useCallback, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import { Card, Row, Col, Badge, Modal, Form, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../api/axios';
import { Endpoints } from '../../api/Endpoints';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    CartesianGrid,
    Legend
} from 'recharts';

const BRAND_NAVY = '#00024f';
const BRAND_BLUE = '#183eeb';
const BRAND_YELLOW = '#ebfe52';
const BRAND_DANGER = '#ff3b3b';
const BRAND_PURPLE = '#2e00a8';
const BRAND_TEAL = '#b6dcde';
const BRAND_GRAY = '#e8e8e8';

const TIER_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'];

const SellerAnalytics = () => {
    const { t } = useTranslation();
    const { isDark } = useContext(ThemeContext) || {};

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [kpis, setKpis] = useState({
        total_ventas_usd: 0,
        total_pedidos_count: 0,
        ticket_promedio_usd: 0,
        total_facturas_pagadas_usd: 0,
        facturas_pagadas_count: 0,
        total_facturas_pendientes_usd: 0,
        facturas_pendientes_count: 0,
        total_facturas_vencidas_usd: 0,
        facturas_vencidas_count: 0,
        total_comisiones_usd: 0,
        tasa_cobranza_porcentaje: 0,
        dso_promedio_dias: 0,
        vendedores_activos_count: 0,
    });
    const [rankingVendedores, setRankingVendedores] = useState([]);
    const [distribucionTramos, setDistribucionTramos] = useState([]);

    const [periodo, setPeriodo] = useState('mes_actual');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedSellerId, setSelectedSellerId] = useState('');
    const [selectedZoneId, setSelectedZoneId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [sellersCatalog, setSellersCatalog] = useState([]);
    const [zonesCatalog, setZonesCatalog] = useState([]);

    const [showTiersModal, setShowTiersModal] = useState(false);
    const [tiersList, setTiersList] = useState([]);
    const [savingTiers, setSavingTiers] = useState(false);

    const [showTargetModal, setShowTargetModal] = useState(false);
    const [targetForm, setTargetForm] = useState({
        vendedor_id: '',
        periodo_tipo: 'mensual',
        fecha_inicio: '',
        fecha_fin: '',
        meta_monto_usd: ''
    });
    const [savingTarget, setSavingTarget] = useState(false);

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedSellerDetail, setSelectedSellerDetail] = useState(null);

    const loadCatalogs = useCallback(async () => {
        try {
            const [usersRes, zonesRes] = await Promise.all([
                axiosInstance.get('/admin/users?per_page=100').catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/admin/zones?per_page=100').catch(() => ({ data: { data: [] } }))
            ]);

            const usersList = usersRes.data?.data?.data || usersRes.data?.data || [];
            const sellersOnly = usersList.filter(u => String(u.rol || '').toLowerCase() === 'vendedor');
            setSellersCatalog(sellersOnly);

            const zList = zonesRes.data?.data?.data || zonesRes.data?.data || [];
            setZonesCatalog(zList);
        } catch (err) {
            console.error('Error cargando catálogos:', err);
        }
    }, []);

    const loadCommissionTiers = useCallback(async () => {
        try {
            const res = await axiosInstance.get(Endpoints.SELLER_COMMISSION_TIERS);
            if (res.data?.success && Array.isArray(res.data?.data)) {
                setTiersList(res.data.data);
            }
        } catch (err) {
            console.error('Error cargando tramos de comisión:', err);
        }
    }, []);

    const fetchDashboardData = useCallback(async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            const params = {
                periodo,
                vendedor_id: selectedSellerId || undefined,
                zone_id: selectedZoneId || undefined,
            };

            if (periodo === 'personalizado') {
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
            }

            const res = await axiosInstance.get(Endpoints.SELLER_ANALYTICS_DASHBOARD, { params });

            if (res.data?.success && res.data?.data) {
                const data = res.data.data;
                setKpis(data.kpis || {});
                setRankingVendedores(data.ranking_vendedores || []);
                setDistribucionTramos(data.distribucion_tramos || []);
            }
        } catch (err) {
            console.error('Error al cargar analítica de vendedores:', err);
            toast.error(t('seller_analytics_error_load', 'No se pudo cargar la información del tablero comercial'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [periodo, dateFrom, dateTo, selectedSellerId, selectedZoneId]);

    useEffect(() => {
        loadCatalogs();
        loadCommissionTiers();
    }, [loadCatalogs, loadCommissionTiers]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const handleSaveTiers = async () => {
        setSavingTiers(true);
        try {
            const res = await axiosInstance.put(Endpoints.SELLER_COMMISSION_TIERS, {
                tiers: tiersList
            });

            if (res.data?.success) {
                toast.success(t('seller_analytics_tiers_saved_success', 'Tramos y porcentajes de comisión actualizados con éxito'));
                setShowTiersModal(false);
                loadCommissionTiers();
                fetchDashboardData(true);
            }
        } catch (err) {
            console.error('Error guardando tramos:', err);
            const msg = err.response?.data?.message || t('seller_analytics_tiers_saved_error', 'Error al guardar los porcentajes de comisión');
            toast.error(msg);
        } finally {
            setSavingTiers(false);
        }
    };

    const handleTierFieldChange = (index, field, value) => {
        setTiersList(prev => {
            const updated = [...prev];
            const currentItem = { ...updated[index], [field]: value };

            if (field === 'dias_desde' || field === 'dias_hasta') {
                const desde = field === 'dias_desde' ? (parseInt(value) || 0) : (parseInt(currentItem.dias_desde) || 0);
                const hastaRaw = field === 'dias_hasta' ? value : currentItem.dias_hasta;
                const hasta = (hastaRaw !== '' && hastaRaw !== null && hastaRaw !== undefined) ? parseInt(hastaRaw) : null;
                
                const rangoStr = hasta !== null ? `(${desde} a ${hasta} días)` : `(${desde} días en adelante)`;
                currentItem.nombre = `Tramo ${index + 1} ${rangoStr}`;
            }

            updated[index] = currentItem;
            return updated;
        });
    };

    const getPeriodDates = (periodType) => {
        const today = new Date();
        if (periodType === 'semanal') {
            const day = today.getDay();
            const diffToMonday = (day === 0 ? -6 : 1) - day;
            const monday = new Date(today);
            monday.setDate(today.getDate() + diffToMonday);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return {
                inicio: monday.toISOString().split('T')[0],
                fin: sunday.toISOString().split('T')[0]
            };
        }

        return {
            inicio: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
            fin: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0]
        };
    };

    const handleOpenTargetModal = (vendedorId = '') => {
        const targetSellerId = vendedorId || (sellersCatalog[0]?.id || '');
        const seller = rankingVendedores.find(s => s.vendedor_id === targetSellerId);
        const currentMeta = seller && seller.meta_mensual_usd > 0 ? seller.meta_mensual_usd : '';
        const dates = getPeriodDates('mensual');

        setTargetForm({
            vendedor_id: targetSellerId,
            periodo_tipo: 'mensual',
            fecha_inicio: dates.inicio,
            fecha_fin: dates.fin,
            meta_monto_usd: currentMeta
        });
        setShowTargetModal(true);
    };

    const handlePeriodTypeChange = (newPeriodType) => {
        const dates = getPeriodDates(newPeriodType);
        const seller = rankingVendedores.find(s => s.vendedor_id === targetForm.vendedor_id);
        const prefilledMeta = seller 
            ? (newPeriodType === 'semanal' 
                ? (seller.meta_semanal_usd > 0 ? seller.meta_semanal_usd : '') 
                : (seller.meta_mensual_usd > 0 ? seller.meta_mensual_usd : ''))
            : '';

        setTargetForm(prev => ({
            ...prev,
            periodo_tipo: newPeriodType,
            fecha_inicio: dates.inicio,
            fecha_fin: dates.fin,
            meta_monto_usd: prefilledMeta || prev.meta_monto_usd
        }));
    };

    const handleTargetSellerChange = (newVendedorId) => {
        const seller = rankingVendedores.find(s => s.vendedor_id === newVendedorId);
        const prefilledMeta = seller 
            ? (targetForm.periodo_tipo === 'semanal' 
                ? (seller.meta_semanal_usd > 0 ? seller.meta_semanal_usd : '') 
                : (seller.meta_mensual_usd > 0 ? seller.meta_mensual_usd : ''))
            : '';

        setTargetForm(prev => ({
            ...prev,
            vendedor_id: newVendedorId,
            meta_monto_usd: prefilledMeta
        }));
    };

    const handleSaveTarget = async (e) => {
        e.preventDefault();
        if (!targetForm.vendedor_id) {
            toast.warning(t('seller_analytics_select_seller_warning', 'Seleccione un vendedor'));
            return;
        }
        if (!targetForm.meta_monto_usd || Number(targetForm.meta_monto_usd) <= 0) {
            toast.warning(t('seller_analytics_valid_amount_warning', 'Ingrese una meta en dólares válida'));
            return;
        }

        setSavingTarget(true);
        try {
            const res = await axiosInstance.post(Endpoints.SELLER_TARGETS, {
                vendedor_id: targetForm.vendedor_id,
                periodo_tipo: targetForm.periodo_tipo,
                fecha_inicio: targetForm.fecha_inicio,
                fecha_fin: targetForm.fecha_fin,
                meta_monto_usd: parseFloat(targetForm.meta_monto_usd)
            });

            if (res.data?.success) {
                toast.success(t('seller_analytics_target_saved_success', 'Meta comercial asignada correctamente'));
                setShowTargetModal(false);
                fetchDashboardData(true);
            }
        } catch (err) {
            console.error('Error guardando meta:', err);
            const msg = err.response?.data?.message || t('seller_analytics_target_saved_error', 'Error al asignar la meta comercial');
            toast.error(msg);
        } finally {
            setSavingTarget(false);
        }
    };

    const handleOpenSellerDetail = (seller) => {
        setSelectedSellerDetail(seller);
        setShowDetailModal(true);
    };

    const filteredRanking = rankingVendedores.filter(s => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            (s.nombre_completo && s.nombre_completo.toLowerCase().includes(term)) ||
            (s.email && s.email.toLowerCase().includes(term)) ||
            (s.zona?.name && s.zona.name.toLowerCase().includes(term))
        );
    });

    const formatUsd = (val) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(val || 0);
    };

    const topSellersChartData = rankingVendedores.slice(0, 7).map(s => ({
        name: (s.nombre_completo || 'Vendedor').split(' ')[0],
        ventas: s.total_ventas_usd,
        cobrado: s.facturas_pagadas_usd,
        comisiones: s.total_comisiones_usd
    }));

    const pieTiersData = distribucionTramos.map((t, idx) => ({
        name: t.nombre,
        value: t.total_cobrado_usd,
        comision: t.comision_devengada_usd,
        porcentaje: t.porcentaje_comision,
        color: TIER_COLORS[idx % TIER_COLORS.length]
    })).filter(t => t.value > 0);

    return (
        <div className="container-fluid py-4" style={{ minHeight: '100vh' }}>
            <ToastContainer position="top-right" autoClose={3500} theme={isDark ? 'dark' : 'light'} />

            <style>{`
                .seller-custom-progress-track {
                    width: 100% !important;
                    height: 7px !important;
                    border-radius: 999px !important;
                    overflow: hidden !important;
                    position: relative !important;
                    display: block !important;
                }

                .seller-custom-progress-track.track-dark,
                .table-hover tbody tr:hover .seller-custom-progress-track.track-dark,
                .table-hover > tbody > tr:hover > * .seller-custom-progress-track.track-dark,
                table.table-hover tbody tr:hover td .seller-custom-progress-track.track-dark {
                    background-color: #273552 !important;
                }

                .seller-custom-progress-track.track-light,
                .table-hover tbody tr:hover .seller-custom-progress-track.track-light,
                .table-hover > tbody > tr:hover > * .seller-custom-progress-track.track-light,
                table.table-hover tbody tr:hover td .seller-custom-progress-track.track-light {
                    background-color: #e2e8f0 !important;
                }

                .seller-custom-progress-fill {
                    height: 100% !important;
                    border-radius: 999px !important;
                    transition: width 0.3s ease !important;
                    display: block !important;
                }

                .seller-custom-progress-fill.fill-warning,
                .table-hover tbody tr:hover .seller-custom-progress-fill.fill-warning,
                .table-hover > tbody > tr:hover > * .seller-custom-progress-fill.fill-warning,
                table.table-hover tbody tr:hover td .seller-custom-progress-fill.fill-warning {
                    background-color: #f59e0b !important;
                }

                .seller-custom-progress-fill.fill-primary,
                .table-hover tbody tr:hover .seller-custom-progress-fill.fill-primary,
                .table-hover > tbody > tr:hover > * .seller-custom-progress-fill.fill-primary,
                table.table-hover tbody tr:hover td .seller-custom-progress-fill.fill-primary {
                    background-color: #183eeb !important;
                }

                .seller-custom-progress-fill.fill-success,
                .table-hover tbody tr:hover .seller-custom-progress-fill.fill-success,
                .table-hover > tbody > tr:hover > * .seller-custom-progress-fill.fill-success,
                table.table-hover tbody tr:hover td .seller-custom-progress-fill.fill-success {
                    background-color: #10b981 !important;
                }

                .seller-unified-input-group {
                    display: flex !important;
                    align-items: center !important;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .seller-unified-input-group:focus-within {
                    border-color: #183eeb !important;
                    box-shadow: 0 0 0 3px rgba(24, 62, 235, 0.15) !important;
                }
                body.dark-theme .seller-unified-input-group:focus-within,
                [data-theme="dark"] .seller-unified-input-group:focus-within {
                    border-color: #38bdf8 !important;
                    box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.2) !important;
                }

                .seller-unified-control {
                    flex: 1 !important;
                    width: 100% !important;
                    height: 100% !important;
                    margin: 0 !important;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                    line-height: normal !important;
                    background-color: transparent !important;
                    vertical-align: middle !important;
                    display: block !important;
                }

                .seller-table-input {
                    margin: 0 !important;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    padding-left: 8px !important;
                    padding-right: 8px !important;
                    line-height: normal !important;
                    vertical-align: middle !important;
                    outline: none !important;
                    box-shadow: none !important;
                    display: block !important;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .seller-table-input:focus {
                    border-color: #183eeb !important;
                    box-shadow: 0 0 0 2px rgba(24, 62, 235, 0.15) !important;
                }
                body.dark-theme .seller-table-input:focus,
                [data-theme="dark"] .seller-table-input:focus {
                    border-color: #38bdf8 !important;
                    box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.2) !important;
                }
            `}</style>

            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <div 
                            className="d-flex align-items-center justify-content-center rounded-3 shadow-sm"
                            style={{ 
                                width: '42px', 
                                height: '42px', 
                                backgroundColor: BRAND_BLUE, 
                                color: '#ffffff' 
                            }}
                        >
                            <i className="mdi mdi-chart-timeline-variant" style={{ fontSize: '1.4rem' }}></i>
                        </div>
                        <h2 className="mb-0 fw-bold" style={{ color: isDark ? '#f8fafc' : BRAND_NAVY, fontSize: '1.65rem' }}>
                            {t('seller_analytics_title', 'Rendimiento y Analítica de Vendedores')}
                        </h2>
                    </div>
                    <p className="text-muted mb-0 small">
                        {t('seller_analytics_subtitle', 'Tablero gerencial de ventas, efectividad de cobranzas, comisiones por pronto pago y metas de facturación.')}
                    </p>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button 
                        variant="outline-primary"
                        className="d-flex align-items-center gap-1 shadow-sm fw-semibold"
                        onClick={() => setShowTiersModal(true)}
                        style={{ borderRadius: '8px' }}
                    >
                        <i className="mdi mdi-tune-vertical"></i>
                        <span>{t('seller_analytics_config_tiers', 'Configurar Comisiones')}</span>
                    </Button>

                    <Button 
                        variant="primary"
                        className="d-flex align-items-center gap-1 shadow-sm fw-semibold text-white"
                        onClick={() => handleOpenTargetModal()}
                        style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE, borderRadius: '8px' }}
                    >
                        <i className="mdi mdi-target"></i>
                        <span>{t('seller_analytics_assign_targets', 'Asignar Metas')}</span>
                    </Button>

                    <Button 
                        variant={isDark ? 'outline-light' : 'outline-secondary'}
                        className="d-flex align-items-center justify-content-center shadow-sm"
                        onClick={() => fetchDashboardData(true)}
                        disabled={refreshing}
                        style={{ width: '38px', height: '38px', borderRadius: '8px', padding: 0 }}
                        title={t('seller_analytics_refresh_data', 'Actualizar datos')}
                    >
                        <i className={`mdi mdi-refresh ${refreshing ? 'mdi-spin' : ''}`} style={{ fontSize: '1.2rem' }}></i>
                    </Button>
                </div>
            </div>

            <Card 
                className="mb-4 border-0 shadow-sm"
                style={{ 
                    borderRadius: '12px', 
                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0'
                }}
            >
                <Card.Body className="p-3">
                    <Row className="g-3 align-items-end">
                        <Col xs={12} sm={6} md={3} lg={3}>
                            <Form.Label className="small fw-semibold text-muted mb-1">
                                <i className="mdi mdi-calendar-range me-1"></i>{t('seller_analytics_filter_period', 'Período')}
                            </Form.Label>
                            <Form.Select 
                                value={periodo} 
                                onChange={(e) => setPeriodo(e.target.value)}
                                className="form-select-sm"
                                style={{
                                    backgroundColor: isDark ? '#1a223f' : '#f8fafc',
                                    color: isDark ? '#ffffff' : '#010028',
                                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                    borderRadius: '8px'
                                }}
                            >
                                <option value="mes_actual">{t('seller_analytics_period_current_month', 'Mes Actual')}</option>
                                <option value="semana_actual">{t('seller_analytics_period_current_week', 'Esta Semana')}</option>
                                <option value="mes_anterior">{t('seller_analytics_period_last_month', 'Mes Anterior')}</option>
                                <option value="hoy">{t('seller_analytics_period_today', 'Hoy')}</option>
                                <option value="personalizado">{t('seller_analytics_period_custom', 'Rango Personalizado')}</option>
                            </Form.Select>
                        </Col>

                        {periodo === 'personalizado' && (
                            <>
                                <Col xs={6} md={2}>
                                    <Form.Label className="small fw-semibold text-muted mb-1">{t('seller_analytics_filter_from', 'Desde')}</Form.Label>
                                    <Form.Control 
                                        type="date"
                                        size="sm"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        style={{
                                            backgroundColor: isDark ? '#1a223f' : '#f8fafc',
                                            color: isDark ? '#ffffff' : '#010028',
                                            borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                            borderRadius: '8px'
                                        }}
                                    />
                                </Col>
                                <Col xs={6} md={2}>
                                    <Form.Label className="small fw-semibold text-muted mb-1">{t('seller_analytics_filter_to', 'Hasta')}</Form.Label>
                                    <Form.Control 
                                        type="date"
                                        size="sm"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        style={{
                                            backgroundColor: isDark ? '#1a223f' : '#f8fafc',
                                            color: isDark ? '#ffffff' : '#010028',
                                            borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                            borderRadius: '8px'
                                        }}
                                    />
                                </Col>
                            </>
                        )}

                        <Col xs={12} sm={6} md={3} lg={3}>
                            <Form.Label className="small fw-semibold text-muted mb-1">
                                <i className="mdi mdi-account-tie me-1"></i>{t('seller_analytics_filter_seller', 'Vendedor')}
                            </Form.Label>
                            <Form.Select 
                                value={selectedSellerId} 
                                onChange={(e) => setSelectedSellerId(e.target.value)}
                                className="form-select-sm"
                                style={{
                                    backgroundColor: isDark ? '#1a223f' : '#f8fafc',
                                    color: isDark ? '#ffffff' : '#010028',
                                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                    borderRadius: '8px'
                                }}
                            >
                                <option value="">{t('seller_analytics_all_sellers', 'Todos los Vendedores')}</option>
                                {sellersCatalog.map(s => (
                                    <option key={s.id} value={s.id}>{s.nombre_completo}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col xs={12} sm={6} md={3} lg={3}>
                            <Form.Label className="small fw-semibold text-muted mb-1">
                                <i className="mdi mdi-map-marker-radius me-1"></i>{t('seller_analytics_filter_zone', 'Zona')}
                            </Form.Label>
                            <Form.Select 
                                value={selectedZoneId} 
                                onChange={(e) => setSelectedZoneId(e.target.value)}
                                className="form-select-sm"
                                style={{
                                    backgroundColor: isDark ? '#1a223f' : '#f8fafc',
                                    color: isDark ? '#ffffff' : '#010028',
                                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                    borderRadius: '8px'
                                }}
                            >
                                <option value="">{t('seller_analytics_all_zones', 'Todas las Zonas')}</option>
                                {zonesCatalog.map(z => (
                                    <option key={z.id} value={z.id}>{z.code ? `[${z.code}] ` : ''}{z.name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {loading ? (
                <div className="py-5 text-center">
                    <LoadingState text={t('seller_analytics_loading', 'Consolidando métricas comerciales...')} />
                </div>
            ) : (
                <>
                    <Row className="g-3 mb-4">
                        <Col xs={12} sm={6} xl={3}>
                            <Card 
                                className="border-0 shadow-sm h-100"
                                style={{ 
                                    borderRadius: '12px', 
                                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                                    borderLeft: `4px solid ${BRAND_BLUE}`
                                }}
                            >
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <span className="text-muted small fw-semibold text-uppercase">
                                                {t('seller_analytics_kpi_sales', 'Total Facturado')}
                                            </span>
                                            <h3 className="fw-bold mb-0 mt-1" style={{ color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                                {formatUsd(kpis.total_ventas_usd)}
                                            </h3>
                                        </div>
                                        <div 
                                            className="d-flex align-items-center justify-content-center rounded-circle"
                                            style={{ width: '40px', height: '40px', backgroundColor: 'rgba(24, 62, 235, 0.12)', color: BRAND_BLUE }}
                                        >
                                            <i className="mdi mdi-cash-multiple" style={{ fontSize: '1.3rem' }}></i>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pt-2 border-top border-light-subtle">
                                        <span className="small text-muted">{kpis.total_pedidos_count} {t('seller_analytics_kpi_orders_billed', 'pedidos facturados')}</span>
                                        <span className="small text-muted">{t('seller_analytics_kpi_avg_ticket', 'Ticket Promedio')}: {formatUsd(kpis.ticket_promedio_usd)}</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} xl={3}>
                            <Card 
                                className="border-0 shadow-sm h-100"
                                style={{ 
                                    borderRadius: '12px', 
                                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                                    borderLeft: '4px solid #10b981'
                                }}
                            >
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <span className="text-muted small fw-semibold text-uppercase">
                                                {t('seller_analytics_kpi_effective_collection', 'Cobranza Efectiva')}
                                            </span>
                                            <h3 className="fw-bold mb-0 mt-1" style={{ color: '#10b981' }}>
                                                {formatUsd(kpis.total_facturas_pagadas_usd)}
                                            </h3>
                                        </div>
                                        <div 
                                            className="d-flex align-items-center justify-content-center rounded-circle"
                                            style={{ width: '40px', height: '40px', backgroundColor: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}
                                        >
                                            <i className="mdi mdi-check-decagram" style={{ fontSize: '1.3rem' }}></i>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pt-2 border-top border-light-subtle">
                                        <span className="small text-muted">{kpis.facturas_pagadas_count} {t('seller_analytics_kpi_paid_of', 'pagadas de')} {kpis.total_pedidos_count} {t('seller_analytics_kpi_total', 'totales')}</span>
                                        <Badge bg="success" className="px-2 py-1">
                                            {kpis.tasa_cobranza_porcentaje}% {t('seller_analytics_kpi_collection_rate', 'cobrado')}
                                        </Badge>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} xl={3}>
                            <Card 
                                className="border-0 shadow-sm h-100"
                                style={{ 
                                    borderRadius: '12px', 
                                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                                    borderLeft: `4px solid ${kpis.total_facturas_vencidas_usd > 0 ? BRAND_DANGER : '#f59e0b'}`
                                }}
                            >
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <span className="text-muted small fw-semibold text-uppercase">
                                                {t('seller_analytics_kpi_pending_collection', 'Por Cobrar / Pendiente')}
                                            </span>
                                            <h3 className="fw-bold mb-0 mt-1" style={{ color: '#f59e0b' }}>
                                                {formatUsd(kpis.total_facturas_pendientes_usd)}
                                            </h3>
                                        </div>
                                        <div 
                                            className="d-flex align-items-center justify-content-center rounded-circle"
                                            style={{ width: '40px', height: '40px', backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}
                                        >
                                            <i className="mdi mdi-clock-alert-outline" style={{ fontSize: '1.3rem' }}></i>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pt-2 border-top border-light-subtle">
                                        <span className="small text-muted">{kpis.facturas_pendientes_count} {t('seller_analytics_kpi_invoices_pending', 'facturas por cobrar')}</span>
                                        {kpis.total_facturas_vencidas_usd > 0 ? (
                                            <Badge bg="danger" className="px-2 py-1">
                                                {t('seller_analytics_kpi_overdue', 'Vencido:')} {formatUsd(kpis.total_facturas_vencidas_usd)}
                                            </Badge>
                                        ) : (
                                            <Badge 
                                                bg={isDark ? "dark" : "secondary"} 
                                                className="px-2 py-1"
                                                style={{ border: isDark ? '1px solid #475569' : 'none' }}
                                            >
                                                {t('seller_analytics_kpi_up_to_date', 'Al día')}
                                            </Badge>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} xl={3}>
                            <Card 
                                className="border-0 shadow-sm h-100"
                                style={{ 
                                    borderRadius: '12px', 
                                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                                    borderLeft: `4px solid ${isDark ? '#a78bfa' : BRAND_PURPLE}`
                                }}
                            >
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <span className="text-muted small fw-semibold text-uppercase">
                                                {t('seller_analytics_kpi_commissions', 'Comisiones Ganadas')}
                                            </span>
                                            <h3 className="fw-bold mb-0 mt-1" style={{ color: isDark ? '#c4b5fd' : BRAND_PURPLE }}>
                                                {formatUsd(kpis.total_comisiones_usd)}
                                            </h3>
                                        </div>
                                        <div 
                                            className="d-flex align-items-center justify-content-center rounded-circle"
                                            style={{ 
                                                width: '40px', 
                                                height: '40px', 
                                                backgroundColor: isDark ? 'rgba(167, 139, 250, 0.15)' : 'rgba(46, 0, 168, 0.12)', 
                                                color: isDark ? '#c4b5fd' : BRAND_PURPLE 
                                            }}
                                        >
                                            <i className="mdi mdi-percent-outline" style={{ fontSize: '1.3rem' }}></i>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pt-2 border-top border-light-subtle">
                                        <span className="small text-muted">{t('seller_analytics_kpi_dso_avg', 'DSO promedio:')} <strong>{kpis.dso_promedio_dias} {t('days', 'días')}</strong></span>
                                        <span className="small text-muted">{kpis.vendedores_activos_count} {t('seller_analytics_active_sellers', 'vendedores')}</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="g-4 mb-4">
                        <Col xs={12} lg={8}>
                            <Card 
                                className="border-0 shadow-sm h-100"
                                style={{ 
                                    borderRadius: '12px', 
                                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                                    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'
                                }}
                            >
                                <Card.Header 
                                    className="bg-transparent border-0 pt-3 px-4 pb-0 d-flex justify-content-between align-items-center"
                                >
                                    <div>
                                        <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                            {t('seller_analytics_chart_comparison', 'Top Vendedores: Facturación vs Cobranza')}
                                        </h5>
                                        <span className="small text-muted">
                                            {t('seller_analytics_chart_comparison_sub', 'Comparativa de volumen colocado frente a dinero efectivamente cobrado')}
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="px-4 py-3" style={{ minHeight: '320px' }}>
                                    {topSellersChartData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={290}>
                                            <BarChart data={topSellersChartData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'} />
                                                <XAxis dataKey="name" stroke={isDark ? '#94a3b8' : '#64748b'} fontSize={12} />
                                                <YAxis stroke={isDark ? '#94a3b8' : '#64748b'} fontSize={12} tickFormatter={(val) => `$${val}`} />
                                                <Tooltip 
                                                    contentStyle={{
                                                        backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                                        borderColor: isDark ? '#334155' : '#e2e8f0',
                                                        borderRadius: '8px',
                                                        color: isDark ? '#f8fafc' : '#0f172a'
                                                    }}
                                                    formatter={(value) => formatUsd(value)}
                                                />
                                                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                                <Bar dataKey="ventas" name={t('seller_analytics_total_sold_label', 'Total Vendido ($)')} fill={BRAND_BLUE} radius={[4, 4, 0, 0]} />
                                                <Bar dataKey="cobrado" name={t('seller_analytics_total_collected_label', 'Total Cobrado ($)')} fill="#10b981" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                            {t('seller_analytics_chart_no_data', 'No hay datos suficientes de ventas para graficar en este período')}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} lg={4}>
                            <Card 
                                className="border-0 shadow-sm h-100"
                                style={{ 
                                    borderRadius: '12px', 
                                    backgroundColor: isDark ? '#13192f' : '#ffffff',
                                    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'
                                }}
                            >
                                <Card.Header 
                                    className="bg-transparent border-0 pt-3 px-4 pb-0"
                                >
                                    <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                        {t('seller_analytics_tiers_distribution', 'Distribución por Tramo de Cobranza')}
                                    </h5>
                                    <span className="small text-muted">
                                        {t('seller_analytics_tiers_sub', 'Incentivo escalonado por velocidad de retorno')}
                                    </span>
                                </Card.Header>
                                <Card.Body className="px-4 py-3 d-flex flex-column justify-content-between">
                                    <div style={{ height: '200px' }}>
                                        {pieTiersData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieTiersData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={45}
                                                        outerRadius={80}
                                                        paddingAngle={4}
                                                        dataKey="value"
                                                    >
                                                        {pieTiersData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip 
                                                        contentStyle={{
                                                            backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                                            borderColor: isDark ? '#334155' : '#e2e8f0',
                                                            borderRadius: '8px'
                                                        }}
                                                        formatter={(value, name, props) => [
                                                            `${formatUsd(value)} (${props.payload.porcentaje}%)`,
                                                            props.payload.name
                                                        ]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
                                                {t('seller_analytics_tiers_empty', 'Sin cobros registrados en los tramos')}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-3">
                                        {distribucionTramos.map((tItem, idx) => (
                                            <div key={tItem.tier_id} className="d-flex align-items-center justify-content-between py-1 border-bottom border-light-subtle small">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span 
                                                        style={{ 
                                                            width: '10px', 
                                                            height: '10px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: TIER_COLORS[idx % TIER_COLORS.length] 
                                                        }}
                                                    ></span>
                                                    <span className="fw-semibold">{tItem.nombre}</span>
                                                    <Badge bg="secondary" className="px-1 py-0" style={{ fontSize: '0.7rem' }}>
                                                        {tItem.porcentaje_comision}%
                                                    </Badge>
                                                </div>
                                                <div className="text-end">
                                                    <div className="fw-bold">{formatUsd(tItem.total_cobrado_usd)}</div>
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {t('seller_analytics_commission_label', 'Comisión:')} {formatUsd(tItem.comision_devengada_usd)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card 
                        className="border-0 shadow-sm mb-4"
                        style={{ 
                            borderRadius: '12px', 
                            backgroundColor: isDark ? '#13192f' : '#ffffff',
                            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'
                        }}
                    >
                        <Card.Header 
                            className="bg-transparent border-0 p-4 pb-2 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3"
                        >
                            <div>
                                <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                    {t('seller_analytics_ranking_title', 'Ranking y Desempeño Comercial de Vendedores')}
                                </h5>
                                <span className="small text-muted">
                                    {t('seller_analytics_ranking_subtitle', 'Comparativa detallada de facturación, efectividad de cobranza, comisiones ganadas y cumplimiento de metas')}
                                </span>
                            </div>

                            <div style={{ maxWidth: '300px', width: '100%' }}>
                                <Form.Control 
                                    type="text"
                                    size="sm"
                                    placeholder={t('seller_analytics_search_placeholder', 'Buscar vendedor o zona...')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        backgroundColor: isDark ? '#1a223f' : '#f8fafc',
                                        color: isDark ? '#ffffff' : '#010028',
                                        borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                        borderRadius: '8px'
                                    }}
                                />
                            </div>
                        </Card.Header>

                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0" style={{ minWidth: '950px' }}>
                                    <thead style={{ backgroundColor: isDark ? '#1a223f' : '#f8fafc' }}>
                                        <tr>
                                            <th className="py-3 px-3 text-center" style={{ width: '60px' }}>#</th>
                                            <th className="py-3 px-3">{t('seller_analytics_th_seller', 'Vendedor')}</th>
                                            <th className="py-3 px-3 text-end">{t('seller_analytics_th_sold', 'Total Vendido ($)')}</th>
                                            <th className="py-3 px-3 text-end">{t('seller_analytics_th_collected', 'Cobrado ($)')}</th>
                                            <th className="py-3 px-3 text-end">{t('seller_analytics_th_pending', 'Por Cobrar ($)')}</th>
                                            <th className="py-3 px-3 text-center">{t('seller_analytics_th_target_progress', 'Cumplimiento Meta')}</th>
                                            <th className="py-3 px-3 text-end">{t('seller_analytics_th_commission', 'Comisión Ganada ($)')}</th>
                                            <th className="py-3 px-3 text-center">{t('seller_analytics_th_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRanking.length > 0 ? (
                                            filteredRanking.map((seller) => {
                                                const pctMeta = seller.cumplimiento_mensual_porcentaje || 0;
                                                const progressVariant = pctMeta >= 100 ? 'success' : pctMeta >= 70 ? 'primary' : 'warning';

                                                return (
                                                    <tr key={seller.vendedor_id} style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
                                                        <td className="py-3 px-3 text-center">
                                                            <span 
                                                                className={`badge rounded-circle p-2 ${
                                                                    seller.ranking_posicion === 1 
                                                                        ? 'bg-warning text-dark' 
                                                                        : seller.ranking_posicion === 2 
                                                                        ? 'bg-secondary text-white' 
                                                                        : seller.ranking_posicion === 3 
                                                                        ? 'bg-danger text-white' 
                                                                        : isDark ? 'bg-dark text-light border border-secondary' : 'bg-light text-dark'
                                                                }`}
                                                                style={{ width: '28px', height: '28px', lineHeight: '14px', fontSize: '0.8rem' }}
                                                            >
                                                                {seller.ranking_posicion}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div 
                                                                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm"
                                                                    style={{ 
                                                                        width: '36px', 
                                                                        height: '36px', 
                                                                        backgroundColor: BRAND_BLUE, 
                                                                        color: '#ffffff',
                                                                        fontSize: '0.85rem'
                                                                    }}
                                                                >
                                                                    {(seller.nombre_completo || 'V').substring(0, 2).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold" style={{ color: isDark ? '#f8fafc' : BRAND_NAVY }}>
                                                                        {seller.nombre_completo}
                                                                    </div>
                                                                    <div className="small text-muted">
                                                                        {seller.zona ? `${seller.zona.name} (${seller.zona.code || 'ZN'})` : t('seller_analytics_no_zone', 'Sin zona')}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3 text-end">
                                                            <div className="fw-bold">{formatUsd(seller.total_ventas_usd)}</div>
                                                            <div className="small text-muted">{seller.total_pedidos_count} {t('seller_analytics_orders_count', 'pedidos')}</div>
                                                        </td>
                                                        <td className="py-3 px-3 text-end">
                                                            <div className="fw-bold text-success">{formatUsd(seller.facturas_pagadas_usd)}</div>
                                                            <div className="small text-muted">{seller.facturas_pagadas_count} {t('seller_analytics_collected_count', 'cobradas')}</div>
                                                        </td>
                                                        <td className="py-3 px-3 text-end">
                                                            <div className="fw-bold text-warning">{formatUsd(seller.facturas_pendientes_usd)}</div>
                                                            {seller.facturas_vencidas_usd > 0 && (
                                                                <div className="small text-danger fw-semibold">
                                                                    {t('seller_analytics_kpi_overdue', 'Vencido:')} {formatUsd(seller.facturas_vencidas_usd)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-3 text-center" style={{ minWidth: '160px' }}>
                                                            {seller.meta_mensual_usd > 0 ? (
                                                                <div>
                                                                    <div className="d-flex justify-content-between small mb-1">
                                                                        <span className="fw-bold" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>{pctMeta}%</span>
                                                                        <span className="text-muted">{t('seller_analytics_meta_label', 'Meta:')} {formatUsd(seller.meta_mensual_usd)}</span>
                                                                    </div>
                                                                    <div className={`seller-custom-progress-track ${isDark ? 'track-dark' : 'track-light'}`}>
                                                                        {pctMeta > 0 ? (
                                                                            <div 
                                                                                className={`seller-custom-progress-fill ${
                                                                                    pctMeta >= 100 ? 'fill-success' : pctMeta >= 70 ? 'fill-primary' : 'fill-warning'
                                                                                }`}
                                                                                style={{ width: `${Math.min(pctMeta, 100)}%` }}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span 
                                                                    className="badge small"
                                                                    style={{
                                                                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.1)',
                                                                        color: isDark ? '#94a3b8' : '#64748b',
                                                                        border: isDark ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(100, 116, 139, 0.2)',
                                                                        borderRadius: '6px',
                                                                        padding: '4px 8px',
                                                                        fontWeight: '500'
                                                                    }}
                                                                >
                                                                    {t('seller_analytics_no_target_badge', 'Sin meta fijada')}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-3 text-end">
                                                            <div className="fw-bold" style={{ color: isDark ? '#c4b5fd' : BRAND_PURPLE, fontSize: '0.98rem' }}>
                                                                {formatUsd(seller.total_comisiones_usd)}
                                                            </div>
                                                            <div className="small text-muted">
                                                                {seller.desglose_tramos?.reduce((acc, t) => acc + (t.pagos_count || 0), 0)} {t('seller_analytics_bonus_payments', 'pagos bonificados')}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3 text-center">
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <button 
                                                                    type="button"
                                                                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center shadow-sm" 
                                                                    style={{ 
                                                                        width: '32px', 
                                                                        height: '32px', 
                                                                        padding: 0,
                                                                        backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : 'rgba(14, 165, 233, 0.08)',
                                                                        color: isDark ? '#38bdf8' : '#0284c7',
                                                                        border: isDark ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(14, 165, 233, 0.2)'
                                                                    }} 
                                                                    onClick={() => handleOpenSellerDetail(seller)}
                                                                    title={t('seller_analytics_view_breakdown', 'Ver desglose de comisiones y tramos')}
                                                                >
                                                                    <i className="mdi mdi-eye-outline" style={{ fontSize: '1.05rem' }}></i>
                                                                </button>
                                                                <button 
                                                                    type="button"
                                                                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center shadow-sm" 
                                                                    style={{ 
                                                                        width: '32px', 
                                                                        height: '32px', 
                                                                        padding: 0,
                                                                        backgroundColor: isDark ? 'rgba(96, 165, 250, 0.12)' : 'rgba(24, 62, 235, 0.08)',
                                                                        color: isDark ? '#60a5fa' : BRAND_BLUE,
                                                                        border: isDark ? '1px solid rgba(96, 165, 250, 0.3)' : '1px solid rgba(24, 62, 235, 0.2)'
                                                                    }} 
                                                                    onClick={() => handleOpenTargetModal(seller.vendedor_id)}
                                                                    title={t('seller_analytics_assign_target', 'Asignar o editar meta comercial')}
                                                                >
                                                                    <i className="mdi mdi-target" style={{ fontSize: '1.05rem' }}></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5 text-muted">
                                                    {t('seller_analytics_no_sellers_found', 'No se encontraron vendedores para los filtros seleccionados')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </>
            )}

            <Modal 
                show={showTiersModal} 
                onHide={() => setShowTiersModal(false)}
                size="lg"
                centered
            >
                <Modal.Header 
                    closeButton 
                    closeVariant="white" 
                    style={{ 
                        backgroundColor: isDark ? '#10172d' : '#00024f', 
                        color: '#ffffff', 
                        borderBottom: '3px solid #183eeb' 
                    }}
                >
                    <Modal.Title className="h5 fw-bold d-flex align-items-center mb-0" style={{ color: '#ffffff' }}>
                        <i className="mdi mdi-tune-vertical me-2" style={{ color: '#ebfe52', fontSize: '1.3rem' }}></i>
                        {t('seller_analytics_modal_tiers_title', 'Configuración de Tramos de Comisión por Cobranza')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                    <p className="small text-muted mb-3">
                        {t('seller_analytics_modal_tiers_desc', 'Los porcentajes son completamente autogestionables. El incentivo premia la velocidad de cobranza según los días transcurridos desde la entrega del pedido hasta la fecha de pago de la factura.')}
                    </p>

                    <div className="table-responsive mb-3">
                        <Table bordered hover responsive className="align-middle mb-0" style={{ borderColor: isDark ? '#2a3759' : '#e2e8f0' }}>
                            <thead className={isDark ? 'table-dark' : 'table-light'}>
                                <tr>
                                    <th>{t('seller_analytics_modal_th_tier_name', 'Nombre del Tramo')}</th>
                                    <th className="text-center" style={{ width: '120px' }}>{t('seller_analytics_modal_th_days_from', 'Días Desde')}</th>
                                    <th className="text-center" style={{ width: '120px' }}>{t('seller_analytics_modal_th_days_to', 'Días Hasta')}</th>
                                    <th className="text-center" style={{ width: '150px' }}>{t('seller_analytics_modal_th_commission_pct', 'Comisión (%)')}</th>
                                    <th className="text-center" style={{ width: '90px' }}>{t('seller_analytics_modal_th_status', 'Estado')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tiersList.map((tier, idx) => (
                                    <tr key={tier.id || idx}>
                                        <td>
                                            <input 
                                                type="text"
                                                className="seller-table-input"
                                                value={tier.nombre}
                                                onChange={(e) => handleTierFieldChange(idx, 'nombre', e.target.value)}
                                                style={{
                                                    backgroundColor: isDark ? '#10172d' : '#ffffff',
                                                    color: isDark ? '#ffffff' : '#010028',
                                                    border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    height: '36px',
                                                    width: '100%',
                                                    fontSize: '0.88rem'
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                type="number"
                                                className="seller-table-input fw-bold"
                                                value={tier.dias_desde}
                                                onChange={(e) => handleTierFieldChange(idx, 'dias_desde', parseInt(e.target.value) || 0)}
                                                style={{
                                                    backgroundColor: isDark ? '#10172d' : '#ffffff',
                                                    color: isDark ? '#ffffff' : '#010028',
                                                    border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    height: '36px',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    fontWeight: '700',
                                                    fontSize: '0.92rem'
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                type="number"
                                                className="seller-table-input fw-bold"
                                                placeholder={t('seller_analytics_modal_no_limit', 'Sin límite')}
                                                value={tier.dias_hasta !== null && tier.dias_hasta !== undefined ? tier.dias_hasta : ''}
                                                onChange={(e) => handleTierFieldChange(idx, 'dias_hasta', e.target.value === '' ? null : parseInt(e.target.value))}
                                                style={{
                                                    backgroundColor: isDark ? '#10172d' : '#ffffff',
                                                    color: isDark ? '#ffffff' : '#010028',
                                                    border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    height: '36px',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    fontWeight: '700',
                                                    fontSize: '0.92rem'
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <div 
                                                className="seller-unified-input-group mx-auto"
                                                style={{
                                                    backgroundColor: isDark ? '#10172d' : '#ffffff',
                                                    border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    height: '36px',
                                                    maxWidth: '135px'
                                                }}
                                            >
                                                <input 
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="100"
                                                    className="seller-unified-control fw-bold px-2"
                                                    value={tier.porcentaje_comision}
                                                    onChange={(e) => handleTierFieldChange(idx, 'porcentaje_comision', parseFloat(e.target.value) || 0)}
                                                    style={{ 
                                                        color: isDark ? '#ffffff' : '#010028',
                                                        textAlign: 'center',
                                                        fontWeight: '700',
                                                        fontSize: '0.92rem'
                                                    }}
                                                />
                                                <div 
                                                    className="d-flex align-items-center justify-content-center px-2 text-muted fw-bold"
                                                    style={{
                                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                                                        borderLeft: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0',
                                                        color: isDark ? '#cbd5e1' : '#64748b',
                                                        height: '100%',
                                                        fontSize: '0.8rem',
                                                        minWidth: '34px',
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    %
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <Form.Check 
                                                type="switch"
                                                id={`tier-active-${idx}`}
                                                checked={tier.is_active}
                                                onChange={(e) => handleTierFieldChange(idx, 'is_active', e.target.checked)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className="p-3 rounded-3" style={{ backgroundColor: isDark ? 'rgba(24, 62, 235, 0.12)' : '#eff6ff', border: isDark ? '1px solid #1e3a8a' : '1px solid #bfdbfe' }}>
                        <div className="d-flex align-items-center gap-2 text-primary fw-bold small mb-1">
                            <i className="mdi mdi-information-outline" style={{ fontSize: '1.1rem' }}></i>
                            <span>{t('seller_analytics_modal_auto_logic_title', 'Lógica de Aplicación Automática')}</span>
                        </div>
                        <p className="small mb-0" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                            {t('seller_analytics_modal_auto_logic_desc', 'Cuando un pago es validado en el sistema, la API calcula la diferencia de días entre la fecha de entrega y la fecha de registro del abono, emparejando automáticamente el tramo correspondiente y devengando el porcentaje definido aquí.')}
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', borderTop: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' }}>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowTiersModal(false)} 
                        disabled={savingTiers}
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                    >
                        {t('cancel', 'Cancelar')}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveTiers} 
                        disabled={savingTiers}
                        style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', borderRadius: '8px', fontWeight: 'bold' }}
                    >
                        {savingTiers ? t('saving', 'Guardando...') : t('seller_analytics_save_config', 'Guardar Configuración')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal 
                show={showTargetModal} 
                onHide={() => setShowTargetModal(false)}
                centered
            >
                <Form onSubmit={handleSaveTarget}>
                    <Modal.Header 
                        closeButton 
                        closeVariant="white"
                        style={{ 
                            backgroundColor: isDark ? '#10172d' : '#00024f', 
                            color: '#ffffff', 
                            borderBottom: '3px solid #183eeb' 
                        }}
                    >
                        <Modal.Title className="h5 fw-bold d-flex align-items-center mb-0" style={{ color: '#ffffff' }}>
                            <i className="mdi mdi-target me-2" style={{ color: '#ebfe52', fontSize: '1.3rem' }}></i>
                            {t('seller_analytics_modal_target_title', 'Asignar Meta Comercial')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                        <div className="mb-3">
                            <Form.Label className="small fw-bold" style={{ color: isDark ? '#cbd5e1' : '#1e293b' }}>
                                {t('seller_analytics_modal_field_seller', 'Vendedor')}
                            </Form.Label>
                            <Form.Select 
                                value={targetForm.vendedor_id}
                                onChange={(e) => handleTargetSellerChange(e.target.value)}
                                required
                                style={{ 
                                    backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                    color: isDark ? '#ffffff' : '#010028',
                                    border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                    borderRadius: '8px'
                                }}
                            >
                                <option value="">{t('seller_analytics_modal_select_seller', 'Seleccione un vendedor...')}</option>
                                {sellersCatalog.map(s => (
                                    <option key={s.id} value={s.id}>{s.nombre_completo}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-3">
                            <Form.Label className="small fw-bold" style={{ color: isDark ? '#cbd5e1' : '#1e293b' }}>
                                {t('seller_analytics_modal_field_period_type', 'Tipo de Período')}
                            </Form.Label>
                            <div className="d-flex gap-4">
                                <Form.Check 
                                    type="radio"
                                    id="period-mensual"
                                    label={t('seller_analytics_period_monthly', 'Mensual')}
                                    name="periodo_tipo"
                                    checked={targetForm.periodo_tipo === 'mensual'}
                                    onChange={() => handlePeriodTypeChange('mensual')}
                                />
                                <Form.Check 
                                    type="radio"
                                    id="period-semanal"
                                    label={t('seller_analytics_period_weekly', 'Semanal')}
                                    name="periodo_tipo"
                                    checked={targetForm.periodo_tipo === 'semanal'}
                                    onChange={() => handlePeriodTypeChange('semanal')}
                                />
                            </div>
                        </div>

                        <Row className="g-2 mb-3">
                            <Col xs={6}>
                                <Form.Label className="small fw-bold" style={{ color: isDark ? '#cbd5e1' : '#1e293b' }}>
                                    {t('seller_analytics_field_start_date', 'Fecha Inicio')}
                                </Form.Label>
                                <Form.Control 
                                    type="date"
                                    value={targetForm.fecha_inicio}
                                    onChange={(e) => setTargetForm({ ...targetForm, fecha_inicio: e.target.value })}
                                    required
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        color: isDark ? '#ffffff' : '#010028',
                                        border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                        borderRadius: '8px'
                                    }}
                                />
                            </Col>
                            <Col xs={6}>
                                <Form.Label className="small fw-bold" style={{ color: isDark ? '#cbd5e1' : '#1e293b' }}>
                                    {t('seller_analytics_field_end_date', 'Fecha Fin')}
                                </Form.Label>
                                <Form.Control 
                                    type="date"
                                    value={targetForm.fecha_fin}
                                    onChange={(e) => setTargetForm({ ...targetForm, fecha_fin: e.target.value })}
                                    required
                                    style={{ 
                                        backgroundColor: isDark ? '#10172d' : '#ffffff', 
                                        color: isDark ? '#ffffff' : '#010028',
                                        border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                        borderRadius: '8px'
                                    }}
                                />
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <Form.Label className="small fw-bold" style={{ color: isDark ? '#cbd5e1' : '#1e293b' }}>
                                {t('seller_analytics_field_target_amount', 'Meta en Dinero ($ USD)')}
                            </Form.Label>
                            <div 
                                className="seller-unified-input-group"
                                style={{
                                    backgroundColor: isDark ? '#10172d' : '#ffffff',
                                    border: isDark ? '1px solid #2a3759' : '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    height: '42px'
                                }}
                            >
                                <div 
                                    className="d-flex align-items-center justify-content-center px-3"
                                    style={{
                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                                        borderRight: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0',
                                        color: isDark ? '#38bdf8' : BRAND_BLUE,
                                        height: '100%'
                                    }}
                                >
                                    <i className="mdi mdi-currency-usd" style={{ fontSize: '1.25rem' }}></i>
                                </div>
                                <input 
                                    type="number"
                                    step="0.01"
                                    min="1"
                                    placeholder="Ej: 5000.00"
                                    value={targetForm.meta_monto_usd}
                                    onChange={(e) => setTargetForm({ ...targetForm, meta_monto_usd: e.target.value })}
                                    required
                                    className="seller-unified-control px-3"
                                    style={{ 
                                        color: isDark ? '#ffffff' : '#010028',
                                        fontWeight: '700',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                <div 
                                    className="d-flex align-items-center justify-content-center px-3 text-muted small fw-bold"
                                    style={{
                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                                        borderLeft: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0',
                                        height: '100%',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                        userSelect: 'none'
                                    }}
                                >
                                    USD
                                </div>
                            </div>
                            <Form.Text className="text-muted small">
                                {t('seller_analytics_target_help', 'Esta meta se visualizará en tiempo real en la app móvil del vendedor.')}
                            </Form.Text>
                        </div>
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', borderTop: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' }}>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowTargetModal(false)} 
                            disabled={savingTarget}
                            style={{ borderRadius: '8px', fontWeight: '600' }}
                        >
                            {t('cancel', 'Cancelar')}
                        </Button>
                        <Button 
                            type="submit" 
                            variant="primary" 
                            disabled={savingTarget}
                            style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', borderRadius: '8px', fontWeight: 'bold' }}
                        >
                            {savingTarget ? t('seller_analytics_saving_target', 'Guardando meta...') : t('seller_analytics_btn_save_target', 'Asignar Meta')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal 
                show={showDetailModal} 
                onHide={() => setShowDetailModal(false)}
                size="lg"
                centered
            >
                <Modal.Header 
                    closeButton 
                    closeVariant="white"
                    style={{ 
                        backgroundColor: isDark ? '#10172d' : '#00024f', 
                        color: '#ffffff', 
                        borderBottom: '3px solid #183eeb' 
                    }}
                >
                    <Modal.Title className="h5 fw-bold d-flex align-items-center mb-0" style={{ color: '#ffffff' }}>
                        <i className="mdi mdi-account-details-outline me-2" style={{ color: '#ebfe52', fontSize: '1.3rem' }}></i>
                        {t('seller_analytics_modal_breakdown_title', 'Desglose Comercial:')} {selectedSellerDetail?.nombre_completo}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                    {selectedSellerDetail && (
                        <>
                            <Row className="g-3 mb-4">
                                <Col xs={6} md={3}>
                                    <div className="p-3 shadow-sm" style={{ backgroundColor: isDark ? '#10172d' : '#f8fafc', border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0', borderRadius: '10px' }}>
                                        <div className="text-muted small fw-semibold">
                                            {t('seller_analytics_modal_total_sales', 'Total Ventas')}
                                        </div>
                                        <div className="h5 fw-bold mb-0 text-primary">{formatUsd(selectedSellerDetail.total_ventas_usd)}</div>
                                    </div>
                                </Col>
                                <Col xs={6} md={3}>
                                    <div className="p-3 shadow-sm" style={{ backgroundColor: isDark ? '#10172d' : '#f8fafc', border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0', borderRadius: '10px' }}>
                                        <div className="text-muted small fw-semibold">
                                            {t('seller_analytics_modal_total_collected', 'Total Cobrado')}
                                        </div>
                                        <div className="h5 fw-bold mb-0 text-success">{formatUsd(selectedSellerDetail.facturas_pagadas_usd)}</div>
                                    </div>
                                </Col>
                                <Col xs={6} md={3}>
                                    <div className="p-3 shadow-sm" style={{ backgroundColor: isDark ? '#10172d' : '#f8fafc', border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0', borderRadius: '10px' }}>
                                        <div className="text-muted small fw-semibold">
                                            {t('seller_analytics_modal_pending', 'Por Cobrar')}
                                        </div>
                                        <div className="h5 fw-bold mb-0 text-warning">{formatUsd(selectedSellerDetail.facturas_pendientes_usd)}</div>
                                    </div>
                                </Col>
                                <Col xs={6} md={3}>
                                    <div className="p-3 shadow-sm" style={{ backgroundColor: isDark ? '#10172d' : '#f8fafc', border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0', borderRadius: '10px' }}>
                                        <div className="text-muted small fw-semibold">
                                            {t('seller_analytics_modal_commissions_won', 'Comisiones Ganadas')}
                                        </div>
                                        <div className="h5 fw-bold mb-0" style={{ color: isDark ? '#c4b5fd' : BRAND_PURPLE }}>
                                            {formatUsd(selectedSellerDetail.total_comisiones_usd)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <h6 className="fw-bold mb-2" style={{ color: isDark ? '#f8fafc' : '#010028' }}>
                                {t('seller_analytics_modal_breakdown_subtitle', 'Desglose de Comisiones por Tramo de Días')}
                            </h6>
                            <div className="table-responsive">
                                <Table bordered hover responsive className="align-middle mb-0" style={{ borderColor: isDark ? '#2a3759' : '#e2e8f0' }}>
                                    <thead className={isDark ? 'table-dark' : 'table-light'}>
                                        <tr>
                                            <th>{t('seller_analytics_modal_th_tier_col', 'Tramo de Cobranza')}</th>
                                            <th className="text-center">{t('seller_analytics_modal_th_pct_col', 'Porcentaje (%)')}</th>
                                            <th className="text-center">{t('seller_analytics_modal_th_payments_col', 'Pagos Registrados')}</th>
                                            <th className="text-end">{t('seller_analytics_modal_th_collected_amount', 'Monto Cobrado ($)')}</th>
                                            <th className="text-end">{t('seller_analytics_modal_th_earned_commission', 'Comisión Devengada ($)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedSellerDetail.desglose_tramos?.map((tItem) => (
                                            <tr key={tItem.tier_id}>
                                                <td className="fw-semibold">{tItem.nombre}</td>
                                                <td className="text-center">
                                                    <Badge bg="primary" style={{ backgroundColor: '#183eeb' }}>{tItem.porcentaje_comision}%</Badge>
                                                </td>
                                                <td className="text-center">{tItem.pagos_count} {t('seller_analytics_invoices_count', 'facturas')}</td>
                                                <td className="text-end fw-semibold">{formatUsd(tItem.total_cobrado_usd)}</td>
                                                <td className="text-end fw-bold" style={{ color: isDark ? '#c4b5fd' : BRAND_PURPLE }}>
                                                    {formatUsd(tItem.comision_devengada_usd)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', borderTop: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' }}>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowDetailModal(false)}
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                    >
                        {t('close', 'Cerrar')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SellerAnalytics;
